const Ractive = require('ractive');
const {
  SphereGeometry,
  TextureLoader,
  MeshBasicMaterial,
  Mesh,
  WebGLRenderer,
  Scene,
  FrontSide,
  BackSide
} = require('three');
const {
  onZoomIn,
  onZoomOut,
  onPanLeft,
  onPanUp,
  onPanDown,
  onPanRight,
  onResetView,
  onFullScreen,
  onExitFullScreen
} = require('./controls');
const template = require('Templates/webgl_viewport.html');
const { OrbitalCamera } = require('./OrbitalCamera');

const createOrbitalMesh = (textureSrc, transparent, flipped) => {
  const geometry = new SphereGeometry(960, 64, 64);
  // Invert geometry so that the material is on the inside.
  if (!flipped) geometry.scale(-1, 1, 1);
  const texture = new TextureLoader().load(textureSrc);
  const material = new MeshBasicMaterial({ map: texture });
  material.transparent = transparent;
  material.side = flipped ? BackSide : FrontSide;
  return new Mesh(geometry, material);
};

// Main animation loop for the renderer.
const animate = (renderer, scene, camera) => {
  requestAnimationFrame(() => animate(renderer, scene, camera));
  camera.controls.update();
  renderer.render(scene, camera);
};

// Initialize WebGL renderer, scene, camera, and events.
const onComplete = function() {
  this.renderer = new WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true
  });

  const viewport = document.getElementById('webgl-viewport');
  this.renderer.setSize(viewport.clientWidth, viewport.clientWidth / 2);
  viewport.appendChild(this.renderer.domElement);

  this.camera = new OrbitalCamera(this.renderer.domElement);

  this.onViewportResize = () => {
    // Unlock aspect ratio if in full screen.
    const height = document.fullscreenElement
      ? viewport.clientHeight
      : viewport.clientWidth / 2;
    this.camera.aspect = viewport.clientWidth / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(viewport.clientWidth, height);
  };
  // Resize camera and renderer when viewport resizes.
  window.addEventListener('resize', this.onViewportResize);
  document.addEventListener('fullscreenchange', this.onViewportResize);

  this.onFullScreenChange = () => {
    this.set('fullScreen', document.fullscreenElement !== null);
  };
  document.addEventListener('fullscreenchange', this.onFullScreenChange);

  this.scene = new Scene();
  this.baseLayer = createOrbitalMesh(
    this.get('baseLayer'),
    false,
    this.get('flipped')
  );
  this.scene.add(this.baseLayer);

  // Update meshes when option, room connection option, or room changes.
  this.observe('activeRoomId', generateMeshes);
  this.observe('userConfig', generateMeshes);
  this.observe('roomConfig', generateMeshes);

  animate(this.renderer, this.scene, this.camera);
};

const generateMeshes = function() {
  const roomConfig = this.get('roomConfig');
  const userConfig = this.get('userConfig');

  // if we do not have access to the configs then we have navigated away
  // from a room and should skip generating meshes.
  if (!roomConfig || !userConfig) { return; }

  this.scene.remove(this.baseLayer);
  this.get('optionMeshes').forEach((mesh) => this.scene.remove(mesh));
  this.get('roomConnectionMeshes').forEach((mesh) => this.scene.remove(mesh));

  // generate base layer mesh
  this.baseLayer = createOrbitalMesh(
    this.get('baseLayer'),
    false,
    this.get('flipped')
  );
  this.scene.add(this.baseLayer);

  // generate option meshes
  const optionMeshes = [];
  const optionGroups = this.get('optionGroups');
  // prettier-ignore
  Object.entries(roomConfig).forEach(([groupId, optionId]) => {
    const optionGroup = optionGroups.find((x) => x.id === groupId);
    if (optionGroup) {
      const option = optionGroup.options.find((x) => x.id == optionId);
      if (option && option.layer_image) {
        optionMeshes.push(createOrbitalMesh(option.layer_image, true, option.flipped));
      }
    }
  });
  optionMeshes.forEach((mesh) => this.scene.add(mesh));
  this.set('optionMeshes', optionMeshes);

  // generate room connection meshes
  const roomConnectionMeshes = [];
  Object.entries(userConfig).forEach(([roomId, config]) => {
    const roomConnection = this.get('roomConnections').find((x) => x.connected_room_id == roomId);
    if (roomConnection) {
      Object.entries(config).forEach(([_groupId, optionId]) => {
        const roomConnectionOption = roomConnection.room_connection_option_data.find((option) => option.option_id == optionId);
        if (roomConnectionOption) {
          roomConnectionMeshes.push(createOrbitalMesh(roomConnectionOption.layer_image_url, true));
        }
      });
    }
  });
  roomConnectionMeshes.forEach((mesh) => this.scene.add(mesh));
  this.set('roomConnectionMeshes', roomConnectionMeshes);
  this.camera.resetPosition();
  if (this.get('flipped')) this.camera.position.x *= -1;
};

const onTeardown = function() {
  window.removeEventListener('resize', this.onViewportResize);
  document.removeEventListener('fullscreenchange', this.onViewportResize);
  document.removeEventListener('fullscreenchange', this.onFullScreenChange);
};

const WebGLViewport = Ractive.extend({
  template,
  data: { fullScreen: false, flipped: false, optionMeshes: [], roomConnectionMeshes: [] },
  oncomplete: onComplete,
  onteardown: onTeardown,
  onZoomIn,
  onZoomOut,
  onPanLeft,
  onPanUp,
  onPanDown,
  onPanRight,
  onResetView,
  onFullScreen,
  onExitFullScreen
});

module.exports = WebGLViewport;
